/**
 * components/EmailReportCard.js
 * Propósito: Mostrar resultado del análisis de un email con score, domainDetails y findings.
 */

import React, { useState } from 'react';
import ScoreBadge from './ScoreBadge';

export default function EmailReportCard({ report }) {
  const [expanded, setExpanded] = useState(false);
  
  const { email, domain, score, findings, domainDetails } = report;
  
  return (
    <div className="card mb-3" style={{ 
      background: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '15px'
    }}>
      <div className="card-body">
        {/* Header con email y score */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="card-title mb-1" style={{ color: '#fff' }}>{email}</h5>
            <small style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Dominio: {domain}</small>
          </div>
          <ScoreBadge score={score} />
        </div>
        
        {/* Detalles del dominio - SIEMPRE visible */}
        {domainDetails && (
          <div className="mb-3 p-3" style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '10px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h6 className="mb-2" style={{ color: '#fff', fontSize: '0.9em' }}>
              📧 Detalles del dominio
            </h6>
            
            <div className="mb-2" style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.85em' }}>
              <strong>Proveedor:</strong> {domainDetails.providerName}
              {domainDetails.providerType === 'public' && (
                <span className="badge bg-info ms-2" style={{ fontSize: '0.75em' }}>Proveedor público</span>
              )}
              {domainDetails.providerType === 'corporate' && (
                <span className="badge bg-secondary ms-2" style={{ fontSize: '0.75em' }}>Dominio corporativo</span>
              )}
            </div>
            
            <div className="mb-2" style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.85em' }}>
              <strong>Infraestructura de correo:</strong> {domainDetails.mxSummary}
            </div>
            
            <div className="mb-3" style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.85em' }}>
              <strong>Ubicación aproximada del servidor:</strong> {domainDetails.hostingRegion}
              {domainDetails.hostingProvider !== 'Proveedor desconocido' && (
                <> – <strong>Proveedor:</strong> {domainDetails.hostingProvider}</>
              )}
            </div>
            
            {/* Autenticación de correo */}
            <div className="mt-2 pt-2" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <h6 className="mb-2" style={{ color: '#fff', fontSize: '0.85em' }}>
                🔒 Autenticación de correo
              </h6>
              
              <div className="mb-1" style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.8em' }}>
                <strong>SPF:</strong> {domainDetails.auth.spf.policySummary}
                {domainDetails.auth.spf.present && (
                  <span className="badge bg-success ms-2" style={{ fontSize: '0.7em' }}>✓</span>
                )}
                {!domainDetails.auth.spf.present && (
                  <span className="badge bg-warning ms-2" style={{ fontSize: '0.7em' }}>✗</span>
                )}
              </div>
              
              <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.8em' }}>
                <strong>DMARC:</strong> {domainDetails.auth.dmarc.policySummary}
                {domainDetails.auth.dmarc.present && (
                  <span className="badge bg-success ms-2" style={{ fontSize: '0.7em' }}>✓</span>
                )}
                {!domainDetails.auth.dmarc.present && (
                  <span className="badge bg-warning ms-2" style={{ fontSize: '0.7em' }}>✗</span>
                )}
                {domainDetails.auth.dmarc.present && domainDetails.auth.dmarc.policy === 'reject' && (
                  <span className="badge bg-success ms-2" style={{ fontSize: '0.7em' }}>Alta seguridad</span>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Botón para ver detalles técnicos */}
        <button 
          className="btn btn-sm btn-outline-light w-100 mt-2"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Ocultar detalles técnicos' : 'Ver detalles técnicos'}
        </button>
        
        {/* Detalles técnicos expandibles */}
        {expanded && (
          <div className="mt-3">
            {findings.critical && findings.critical.length > 0 && (
              <div className="mb-3">
                <h6 style={{ color: '#dc3545' }}>🔴 Crítico</h6>
                <ul className="list-unstyled">
                  {findings.critical.map((f, i) => (
                    <li key={i} className="mb-2" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                      <strong>{f.type}:</strong> {f.message}
                      {f.recommendation && (
                        <div style={{ fontSize: '0.85em', color: 'rgba(255, 255, 255, 0.7)' }}>
                          💡 {f.recommendation}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {findings.warnings && findings.warnings.length > 0 && (
              <div className="mb-3">
                <h6 style={{ color: '#ffc107' }}>⚠️ Advertencias</h6>
                <ul className="list-unstyled">
                  {findings.warnings.map((f, i) => (
                    <li key={i} className="mb-2" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                      <strong>{f.type}:</strong> {f.message}
                      {f.recommendation && (
                        <div style={{ fontSize: '0.85em', color: 'rgba(255, 255, 255, 0.7)' }}>
                          💡 {f.recommendation}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {findings.improvements && findings.improvements.length > 0 && (
              <div className="mb-3">
                <h6 style={{ color: '#0dcaf0' }}>ℹ️ Información</h6>
                <ul className="list-unstyled">
                  {findings.improvements.map((f, i) => (
                    <li key={i} className="mb-2" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                      <strong>{f.type}:</strong> {f.message}
                      {f.recommendation && (
                        <div style={{ fontSize: '0.85em', color: 'rgba(255, 255, 255, 0.7)' }}>
                          💡 {f.recommendation}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
